import { useCallback, useEffect, useState } from 'react';
import { jwtDecode as jwt_decode } from 'jwt-decode';
import { useRefreshToken } from '@/context/RefreshTokenContext';
import { queryClient } from '@/queryClient';
import {
  getActiveToken,
  setPreviewSession,
  clearPreviewSession,
} from './tokenStorage';
import { clearAllStorageAndState } from './storageCleanup';

export interface DecodedToken {
  user_id: string;
  company_id?: string;
  company_name?: string;
  enablecustomerleg?: boolean;
  ai_enable?: boolean;
  is_company_owner?: boolean;
  is_admin: boolean;
  account_type?: string;
  is_payment_due: boolean;
  exp: number;
  email: string;
  name: string;
  user_role: string;
  is_onboarded?: boolean;
  badge_color?: string;
  dark_mode_enabled?: boolean;
  time_zone_enable: boolean;
  user_time_zone?: string;
  date_format?: string;
  currency?: string;
  role?: string;
  notification_options: string;
  ai_static_message: boolean;
  ai_email_static_message: boolean;
  phone: string;
  preferred_language: string;
  chatbot?: boolean;
  ai_insights_chatbot?: boolean;
  agentcore_chatbot?: boolean;
  logiblocks?: boolean;
  alerts_enabled?: boolean;
  one_time_messages_enabled?: boolean;
  include_sensitive_products?: boolean;
  is_preview?: boolean;
  preview_mode?: string;
  preview_target_user_id?: string;
  preview_actor_user_id?: string;
  preview_audit_id?: string;
  per_shipment_geo_fence_enabled?: boolean;
  temperature_gxp_enabled?: boolean;
}

// Shared with StatsigAppProvider so the shape fed to Statsig stays identical
// whether it's built from the app-root client construction or pushed in
// directly from the login flow (see LoginForm.tsx).
export function toStatsigUser(decoded: DecodedToken | null) {
  return {
    userID: decoded?.user_id,
    email: decoded?.email,
    custom: { company_id: decoded?.company_id },
  };
}

export interface PreviewState {
  isPreview: boolean;
  previewMode: 'user' | 'role' | '';
}

interface UseTokenReturn {
  setUserId: (userId: string | null) => void;
  setToken: (token: string | null) => void;
  getToken: () => string | null;
  saveLoginUser: (token: string) => void;
  token: string | null;
  removeToken: () => void;
  userLoginObject: string | null;
  decodedUserToken: () => DecodedToken | null;
  checkTokenExpiry: () => string | null;
  getUserID: () => string | null;
  userID: string | null;
  getPreviewState: () => PreviewState;
  /**
   * Persists a "View As User" / role preview session to this tab's
   * sessionStorage only, leaving the admin's real localStorage token
   * (and every other open tab) untouched.
   */
  savePreviewSession: (access: string, refresh?: string | null) => void;
  /**
   * Ends the tab-local preview session. Only clears sessionStorage; never
   * touches the admin's real localStorage token.
   */
  endPreviewSession: () => void;
}

export default function useToken(): UseTokenReturn {
  const { startRefresh } = useRefreshToken();
  const getToken = useCallback((): string | null => {
    const tokenData = getActiveToken();
    return tokenData ? tokenData : null;
  }, []);

  const checkTokenExpiry = useCallback((): string | null => {
    const userToken = getActiveToken();
    if (userToken) {
      // startRefresh(userToken);
      return userToken;
    } else {
      return null;
    }
  }, []);

  const decodedUserToken = useCallback((): DecodedToken | null => {
    const userToken = getActiveToken();
    if (userToken) {
      const decodedToken = jwt_decode<DecodedToken>(userToken);
      const expDate = new Date(decodedToken.exp * 1000);
      if (expDate < new Date()) {
        clearAllStorageAndState({
          broadcast: true,
          navigateToLogin: true,
        }).catch(console.error);
        return null;
      }
      return expDate > new Date()
        ? { ...decodedToken, exp: expDate.getTime() / 1000 }
        : null;
    } else {
      return null;
    }
  }, []);

  /**
   * Returns the current preview session state derived from the JWT.
   * Centralises the `is_preview` / `preview_mode` reads so every consumer
   * goes through a single, consistent helper instead of manually decoding.
   */
  const getPreviewState = useCallback((): PreviewState => {
    const decoded = decodedUserToken();
    return {
      isPreview: Boolean(decoded?.is_preview),
      previewMode: (decoded?.preview_mode as PreviewState['previewMode']) || '',
    };
  }, [decodedUserToken]);

  const decodeUserId = useCallback((): string | null => {
    const userToken = getActiveToken();
    if (userToken) {
      const decodedToken = jwt_decode<DecodedToken>(userToken);
      return decodedToken.user_id;
    } else {
      return null;
    }
  }, []);
  const [userId, setUserId] = useState<string | null>(decodeUserId());
  const [token, setToken] = useState(() => getActiveToken());
  useEffect(() => {
    if (token) startRefresh(token);
  }, [token, startRefresh]);
  const [userLoginObject] = useState<string | null>(
    getToken() ? getToken() : null
  );

  const saveToken = useCallback(
    (userToken: string) => {
      localStorage.setItem('@token', userToken);
      setToken(userToken);

      if (userToken) startRefresh(userToken);
    },
    [startRefresh]
  );

  const removeToken = useCallback(() => {
    localStorage.removeItem('@token');
    setToken(null);
    // Without a full page reload on logout, cached queries (e.g. profile)
    // would otherwise survive in memory and could briefly serve a stale/
    // previous user's data if a different account logs in on the same tab.
    queryClient.clear();
  }, []);

  const savePreviewSession = useCallback(
    (access: string, refresh?: string | null) => {
      setPreviewSession(access, refresh);
      // Reflects the preview token in this tab's in-memory state so the
      // refresh-scheduling effect above and any consumer of `token` picks
      // up the previewed identity, without ever writing to localStorage.
      setToken(access);
      if (access) startRefresh(access);
    },
    [startRefresh]
  );

  const endPreviewSession = useCallback(() => {
    clearPreviewSession();
    // Fall back to whatever the admin's real localStorage token is (it was
    // never overwritten by the preview session).
    setToken(localStorage.getItem('@token'));
  }, []);

  return {
    setUserId,
    setToken,
    getToken,
    saveLoginUser: saveToken,
    token,
    removeToken,
    userLoginObject,
    decodedUserToken,
    checkTokenExpiry,
    getUserID: decodeUserId,
    userID: userId,
    getPreviewState,
    savePreviewSession,
    endPreviewSession,
  };
}
